import { updateNickname } from '#/util/settings';

export default class ClientEventManager {
    private ircClient: any;

    constructor(ircClient: any) {
        this.ircClient = ircClient;
    }

    initialize(): void {
        this.setupTabEvents();
        this.setupMessageInputEvents();
        this.setupConnectionEvents();
        this.setupChannelListEvents();
        this.setupTabDragAndDrop();
        this.setupSettingsEvents();
    }

    private setupTabEvents(): void {
        document.addEventListener('click', (e) => {
            const tabElement = (e.target as HTMLElement).closest('.irc-tab') as HTMLElement;
            if (tabElement && !(e.target as HTMLElement).classList.contains('irc-tab-close')) {
                const tab = this.ircClient.tabManager.getAllTabs().get(tabElement.dataset.tabId!);
                if (tab) {
                    this.ircClient.tabManager.setActiveTab(tab);
                }
            }
        });

        document.addEventListener('click', (e) => {
            const closeBtn = (e.target as HTMLElement).closest('.irc-tab-close') as HTMLElement;
            if (closeBtn) {
                e.stopPropagation();
                const tabElement = closeBtn.closest('.irc-tab') as HTMLElement;
                if (tabElement) {
                    const tab = this.ircClient.tabManager.getAllTabs().get(tabElement.dataset.tabId!);
                    if (tab) {
                        this.ircClient.tabManager.removeTab(tab);
                    }
                }
            }
        });
    }

    private setupMessageInputEvents(): void {
        document.addEventListener('keypress', (e) => {
            const target = e.target as HTMLElement;
            if (target.classList.contains('irc-input') && (e as KeyboardEvent).key === 'Enter') {
                const input = target as HTMLInputElement;
                const message = input.value.trim();
                if (message) {
                    const tabId = input.dataset.tabId!;
                    const tab = this.ircClient.tabManager.getAllTabs().get(tabId);
                    if (tab) {
                        this.ircClient.messageHandler.sendMessage(tab, message);
                        input.value = '';
                    }
                }
            }
        });
    }

    private setupConnectionEvents(): void {
        document.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            if (target.id === 'irc-connect') {
                this.ircClient.connectionHandler.connect();
            } else if (target.id === 'irc-disconnect') {
                const quitMessage = 'LostHQ IRC Client';
                this.ircClient.connectionHandler.sendIRCCommand('QUIT', quitMessage);
                this.ircClient.connectionHandler.disconnect();
                this.ircClient.tabManager.closeAllTabs();
            }
        });
    }

    private setupChannelListEvents(): void {
        document.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            if (target.textContent === 'Refresh' && target.closest('.irc-channel-list')) {
                this.ircClient.connectionHandler.sendIRCCommand('LIST');
            }
        });
    }

    private setupTabDragAndDrop(): void {
        const tabBar = document.getElementById('irc-tabbar');
        if (!tabBar) return;

        tabBar.addEventListener('dragover', (e) => {
            e.preventDefault();
            const draggingTab = document.querySelector('.irc-tab.dragging') as HTMLElement;
            if (!draggingTab) return;

            const afterElement = this.getDragAfterElement(tabBar, e.clientX);

            tabBar.querySelectorAll('.irc-tab').forEach(tab => {
                tab.classList.remove('drag-over');
            });

            if (afterElement == null) {
                const allTabs = [...tabBar.querySelectorAll('.irc-tab')] as HTMLElement[];
                const lastTab = allTabs[allTabs.length - 1];
                if (lastTab && lastTab !== draggingTab) {
                    lastTab.insertAdjacentElement('afterend', draggingTab);
                }
            } else {
                if (afterElement !== draggingTab) {
                    afterElement.classList.add('drag-over');
                    tabBar.insertBefore(draggingTab, afterElement);
                }
            }
        });

        tabBar.addEventListener('dragleave', (e) => {
            if (!tabBar.contains(e.relatedTarget as Node)) {
                tabBar.querySelectorAll('.irc-tab').forEach(tab => {
                    tab.classList.remove('drag-over');
                });
            }
        });

        tabBar.addEventListener('drop', (e) => {
            e.preventDefault();
            tabBar.querySelectorAll('.irc-tab').forEach(tab => {
                tab.classList.remove('drag-over');
            });
        });
    }

    addDragListeners(tabElement: HTMLElement): void {
        let dragTimer: NodeJS.Timeout | null = null;
        let startX = 0;
        let startY = 0;
        let isDragMode = false;

        tabElement.addEventListener('mousedown', (e) => {
            if ((e.target as HTMLElement).classList.contains('irc-tab-close')) {
                return;
            }

            startX = e.clientX;
            startY = e.clientY;
            isDragMode = false;

            dragTimer = setTimeout(() => {
                tabElement.draggable = true;
                isDragMode = true;
            }, 150);

            const handleMouseMove = (e: MouseEvent) => {
                const deltaX = Math.abs(e.clientX - startX);
                const deltaY = Math.abs(e.clientY - startY);

                if (deltaX > 5 || deltaY > 5) {
                    if (dragTimer) clearTimeout(dragTimer);
                    tabElement.draggable = true;
                    isDragMode = true;
                    document.removeEventListener('mousemove', handleMouseMove);
                }
            };

            const handleMouseUp = () => {
                if (dragTimer) clearTimeout(dragTimer);
                if (!isDragMode) {
                    tabElement.draggable = false;
                }
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        });

        tabElement.addEventListener('dragstart', (e) => {
            tabElement.classList.add('dragging');
            (e as DragEvent).dataTransfer!.effectAllowed = 'move';
            (e as DragEvent).dataTransfer!.setData('text/plain', tabElement.dataset.tabId!);

            const tabBar = document.getElementById('irc-tabbar');
            if (tabBar) {
                tabBar.classList.add('drag-active');
            }
        });

        tabElement.addEventListener('dragend', (e) => {
            tabElement.classList.remove('dragging');
            tabElement.draggable = false;
            const tabBar = document.getElementById('irc-tabbar');
            if (tabBar) {
                tabBar.classList.remove('drag-active');
            }

            document.querySelectorAll('.irc-tab').forEach(tab => {
                tab.classList.remove('drag-over');
            });
        });
    }

    private getDragAfterElement(container: HTMLElement, x: number): HTMLElement | null {
        const draggableElements = [...container.querySelectorAll('.irc-tab.draggable:not(.dragging)')] as HTMLElement[];

        return draggableElements.reduce((closest: any, child) => {
            const box = child.getBoundingClientRect();
            const offset = x - box.left - box.width / 2;

            if (offset < 0 && offset > closest.offset) {
                return { offset: offset, element: child };
            } else {
                return closest;
            }
        }, { offset: Number.NEGATIVE_INFINITY }).element;
    }

    private setupSettingsEvents(): void {
        document.addEventListener('input', (e) => {
            const target = e.target as HTMLElement;
            if (target.id === 'irc-nickname') {
                const input = target as HTMLInputElement;
                updateNickname(input.value.trim());
                this.ircClient.settings.nickname = input.value.trim();
            }
        });
    }
}